import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, TrendingUp, Users, Calculator, PieChart, Target, Bot, Sparkles, ArrowUp, Menu, X } from 'lucide-react';
import './components/FinancialStrategy.css';

interface HomePageProps {
  onStartSimulator: () => void;
  onStartChat: () => void;
  onStartInscription: () => void;
  onOpenMonCompte: () => void;
  onStartSimulateurInvestissement: () => void;
  onStartSimulateurProduits: () => void;
  onStartSimulateurStrategies: () => void;
  onStartSimulateurBesoins: () => void;
  onStartGuidedInvestment: () => void;
  onStartNosSimulateurs: () => void;
  onStartProfilRisque: () => void;
  onStartTour: (tourType: 'homepage' | 'simulator-investment' | 'simulator-products' | 'simulator-strategies' | 'simulator-needs') => void;
  onNavigateLandingBasic: () => void;
  onNavigateLandingExpert: () => void;
  onNavigateOption1?: () => void;
  onNavigateOption2?: () => void;
  onNavigateClientSoeasi: () => void;
  onStartProduitsExplication: () => void;
}

const HomePage: React.FC<HomePageProps> = ({ onStartSimulator, onStartChat, onStartInscription, onOpenMonCompte, onStartSimulateurInvestissement, onStartSimulateurProduits, onStartSimulateurStrategies, onStartSimulateurBesoins, onStartGuidedInvestment, onStartNosSimulateurs, onStartProfilRisque, onStartTour, onNavigateLandingBasic, onNavigateLandingExpert, onNavigateOption1, onNavigateOption2, onNavigateClientSoeasi, onStartProduitsExplication }) => {
  // État pour afficher/masquer le bouton scroll to top
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // État pour le menu mobile
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Effect pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fonction pour remonter en haut
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="hero-background">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <div className="service-icon-teal">
                <PieChart className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold hero-title-gradient">
                SOeasi
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#services" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">À propos</a>
              {onNavigateOption1 && <button onClick={onNavigateOption1} className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Option 1</button>}
              {onNavigateOption2 && <button onClick={onNavigateOption2} className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Option 2</button>}
              <a href="#projets" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Projets</a>
              <a href="#tutos" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">Tutos</a>
              <a href="#faq" className="text-gray-600 hover:text-teal-600 font-medium transition-colors">FAQ</a>
            </nav>
            <div className="flex items-center justify-end space-x-2 flex-shrink-0">
              {/* Boutons desktop */}
              <div className="hidden md:flex items-center space-x-4">
                <button 
                  onClick={onOpenMonCompte}
                  className="px-4 py-2 text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap"
                  style={{
                    backgroundColor: '#1e4586',
                    color: 'white',
                    boxShadow: '0 0 10px rgba(30, 69, 134, 0.3)',
                    border: '1px solid rgba(30, 69, 134, 0.5)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(30, 69, 134, 0.6)';
                    e.currentTarget.style.backgroundColor = '#2d5aa0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 10px rgba(30, 69, 134, 0.3)';
                    e.currentTarget.style.backgroundColor = '#1e4586';
                  }}
                >
                  Mon compte
                </button>
                <button 
                  onClick={onStartInscription}
                  className="btn-primary whitespace-nowrap"
                >
                  Inscrivez-vous
                </button>
              </div>
              
              {/* Menu mobile avec boutons compacts */}
              <div className="md:hidden flex items-center space-x-1">
                <button 
                  onClick={onOpenMonCompte}
                  className="px-2 py-1 text-xs font-medium transition-all duration-300 rounded-md whitespace-nowrap"
                  style={{
                    backgroundColor: '#1e4586',
                    color: 'white',
                    fontSize: '11px'
                  }}
                >
                  Compte
                </button>
                <button 
                  onClick={onStartInscription}
                  className="px-2 py-1 text-xs font-medium transition-all duration-300 rounded-md whitespace-nowrap bg-gradient-to-r from-teal-500 to-blue-500 text-white"
                  style={{
                    fontSize: '11px'
                  }}
                >
                  S'inscrire
                </button>
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 rounded-lg text-gray-600 hover:text-teal-600 hover:bg-gray-100 transition-colors border border-gray-300"
                  style={{ minWidth: '40px', minHeight: '40px' }}
                >
                  {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Menu mobile */}
        <div className={`md:hidden bg-white border-t border-gray-200 shadow-lg transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-4 py-4 space-y-3">
            {/* Navigation links */}
            <div className="divide-y divide-gray-200">
              <a 
                href="#services" 
                className="block px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                À propos
              </a>
              {onNavigateOption1 && (
                <button 
                  onClick={() => {
                    onNavigateOption1();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                >
                  Option 1
                </button>
              )}
              {onNavigateOption2 && (
                <button 
                  onClick={() => {
                    onNavigateOption2();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                >
                  Option 2
                </button>
              )}
              <a 
                href="#projets" 
                className="block px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Projets
              </a>
              <a 
                href="#tutos" 
                className="block px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Tutos
              </a>
              <a 
                href="#faq" 
                className="block px-4 py-3 text-gray-700 hover:text-teal-600 hover:bg-teal-50 font-medium transition-all"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                FAQ
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-block bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  🌐 Plateforme 100% digitale et 100% sans Riba
                </div>
                <h1 className="hero-title">
                  Investissez et épargnez dans votre{' '}
                  <span className="hero-title-gradient">
                    avenir financier
                  </span>
                </h1>
                <p className="hero-description">
                  SOeasi est votre plateforme digitale d'investissement et d'épargne 100% en ligne, 100% sans Riba. 
                  Accédez à des opportunités exclusives dès 50€ : immobilier, finance éthique, 
                  investissements forestiers et bien plus encore.
                </p>
              </div>

             

              {/* Features */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="feature-icon-teal">
                    <span className="text-2xl font-bold text-teal-600">50€</span>
                  </div>
                  <h3 className="font-semibold text-gray-800">Dès 50€</h3>
                  <p className="text-sm text-gray-600">Investissement minimum</p>
                </div>
                <div className="text-center">
                  <div className="feature-icon-blue">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Sécurisé</h3>
                  <p className="text-sm text-gray-600">Plateforme fiable</p>
                </div>
                <div className="text-center">
                  <div className="feature-icon-purple">
                    <Target className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">100% En ligne</h3>
                  <p className="text-sm text-gray-600">Plateforme digitale</p>
                </div>
              </div>
            </div>

            {/* Image du téléphone SOeasi avec animation défilante */}
            <div className="relative">
              <div className="phone-container">
                <div className="phone-scroll-container">
                  <img 
                    src="phoneEasi.webp"
                    alt="Épargne sans Riba - Application mobile SOeasi"
                    className="phone-image-scroll"
                  />
                </div>

                {/* Éléments flottants animés */}
                <div className="floating-element floating-element-green">
                  <span className="text-white font-bold text-sm">+21%</span>
                </div>

                <div className="floating-element floating-element-blue">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>

                <div className="floating-element floating-element-purple">
                  <Shield className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>




      {/* Section Processus en 4 Étapes - Modernisée */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Bâtissez votre avenir financier,{' '}
              <span className="text-teal-600">étape par étape.</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              De la compréhension de vos besoins à la concrétisation de vos projets, notre processus 
              simple et intelligent vous guide vers un futur financier serein.
            </p>
          </div>

          {/* Journey Simple et Lisse */}
          <div className="relative">
            {/* Fils courbés animés - Pattern en Z */}
            <div className="hidden md:block journey-curved-connection">
              {/* Connexion 1->2 - De haut (étape 1) vers bas (étape 2) */}
              <div className="curved-path curved-path-1">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 10 Q 50 30 100 50" 
                    className="animated-path delay-1"
                  />
                </svg>
              </div>
              
              {/* Connexion 2->3 - De bas (étape 2) vers haut (étape 3) */}
              <div className="curved-path curved-path-2">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 50 Q 50 20 100 10" 
                    className="animated-path delay-2"
                  />
                </svg>
              </div>
              
              {/* Connexion 3->4 - De haut (étape 3) vers bas (étape 4) */}
              <div className="curved-path curved-path-3">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 10 Q 50 35 100 45" 
                    className="animated-path delay-3"
                  />
                </svg>
              </div>
            </div>

            {/* Étapes */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 relative z-10">
              
              {/* Étape 1 - Vos Besoins */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container teal-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Couple discutant de leurs projets"
                    />
                  </div>
              
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600 transition-colors duration-500">Vos Besoins</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Nous commençons par comprendre vos projets de vie et vos objectifs financiers.
                  </p>
                </div>
              </div>

              {/* Étape 2 - Votre Stratégie */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container blue-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Graphiques financiers sur écran"
                    />
                  </div>
                
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-500">Votre Stratégie</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Notre expertise élabore un plan personnalisé adapté à votre profil et vos ambitions.
                  </p>
                </div>
              </div>

              {/* Étape 3 - L'Investissement */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container emerald-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Projet immobilier d'investissement"
                    />
                  </div>
                 
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors duration-500">Votre Investissement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Mise en œuvre avec des produits sélectionnés pour faire prospérer votre patrimoine.
                  </p>
                </div>
              </div>

              {/* Étape 4 - Avenir Serein */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container indigo-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Paysage paisible avec champ verdoyant"
                    />
                  </div>
                  
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors duration-500">Votre Avenir Serein</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Profitez d'un futur financier solide avec une parfaite tranquillité d'esprit.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12">
              <button
                onClick={onStartSimulateurBesoins}
                className="group bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:from-teal-700 hover:to-emerald-700 transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 mx-auto"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Commencer mon diagnostic</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* Section Choisissez entre SCPI, GFI ou Sukuk */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Contenu */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold">
                  <div className="w-2 h-2 bg-teal-500 rounded-full mr-2 animate-pulse"></div>
                  Produits d'Investissement Conformes
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  Choisissez entre{' '}
                  <span style={{ 
                    background: 'linear-gradient(135deg, #0d9488 0%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent'
                  }}>
                    SCPI, GFI ou Sukuk
                  </span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Trois solutions d'investissement adaptées à tous les profils. Comparez les rendements, les risques et choisissez votre allocation idéale.
                </p>
              </div>

              <div className="pt-4">
                <button
                  onClick={onStartSimulateurProduits}
                  className="group bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <PieChart className="w-6 h-6" />
                  <span>Comparer les produits</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Photos des 3 produits avec rendements */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* SCPI */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="Laboratoire SCPI - Recherche et innovation"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      SCPI
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <div className="text-emerald-600 text-2xl font-bold">4.5%</div>
                      <div className="text-gray-600 text-xs">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Immobilier Locatif</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Investissement dans l'immobilier commercial et résidentiel avec revenus locatifs garantis.
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Risque: Modéré</span>
                    <span className="text-emerald-600 font-semibold">↗ +0.3%</span>
                  </div>
                </div>
              </div>

              {/* GFI */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="GFI - Forêts et investissement écologique"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      GFI
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <div className="text-blue-600 text-2xl font-bold">5.5%</div>
                      <div className="text-gray-600 text-xs">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Fonds de Placement</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Portefeuille diversifié d'actifs financiers gérés par des experts avec performance optimisée.
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Risque: Élevé</span>
                    <span className="text-blue-600 font-semibold">↗ +0.8%</span>
                  </div>
                </div>
              </div>

              {/* Sukuk */}
              <div className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=90"
                    alt="Sukuk - Maison moderne et résidentiel"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Sukuk
                    </span>
                  </div>
                  <div className="absolute bottom-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                      <div className="text-amber-600 text-2xl font-bold">7.0%</div>
                      <div className="text-gray-600 text-xs">Rendement</div>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Finance Islamique</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Investissement conforme à la Sharia, basé sur des actifs tangibles et des principes éthiques.
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Risque: Faible</span>
                    <span className="text-amber-600 font-semibold">→ Stable</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projets Section */}
      <section id="projets" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Projets d'investissement en cours
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez nos opportunités d'investissement immobilier sélectionnées avec soin 
              dans les grandes métropoles françaises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Projet 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Résidence Confluence Lyon"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    🏢 Populaire
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    12.8% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Résidence Confluence</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lyon 2ème (69) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Appartement T3 de 64m² en plein cœur du quartier Confluence, 
                  proche des transports et commerces.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">24 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">87%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full w-[87%]"></div>
                  </div>
                </div>
                <button className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300">
                  Investir maintenant
                </button>
              </div>
            </div>

            {/* Projet 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Euratechnologies Park Lille"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    🌱 Éco-responsable
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    11.5% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Euratechnologies Park</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lille Métropole (59) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Appartement T2 de 46m² dans une résidence moderne, 
                  proche du pôle technologique d'Euratechnologies.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">36 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">52%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full w-[52%]"></div>
                  </div>
                </div>
                <button className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300">
                  Investir maintenant
                </button>
              </div>
            </div>

            {/* Projet 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Part-Dieu Business Lyon"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ⭐ Nouveau
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    13.2% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Part-Dieu Business</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lyon 3ème (69) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Studio de 28m² dans le quartier d'affaires de Part-Dieu, 
                  idéal pour investissement locatif étudiant.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">18 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">15%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full w-[15%]"></div>
                  </div>
                </div>
                <button className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300">
                  Investir maintenant
                </button>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Plus de 25 projets d'investissement disponibles</p>
            <button className="border-2 border-teal-500 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-all duration-300 flex items-center space-x-2 mx-auto">
              <span>Voir tous les projets</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Section Stratégie Patrimoniale */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Contenu textuel */}
            <div className="order-2 lg:order-1 space-y-8">
              <div className="space-y-6 animate-fadeInLeft">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
                  Une stratégie <br />
                  patrimoniale <br />
                  <span className="text-emerald-600">sereine et durable.</span>
                </h2>
                
                <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                  Notre technologie d'analyse avancée décode votre profil 
                  d'investisseur pour construire un plan d'investissement solide, 
                  adapté à vos ambitions et à votre tranquillité d'esprit.
                </p>
              </div>

              {/* Points clés avec icônes */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4 animate-slideInUp" style={{animationDelay: '0.2s'}}>
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Analyse de Profil Détaillée</h3>
                    <p className="text-gray-600 text-sm">
                      Nous évaluons précisément votre situation pour une stratégie sur mesure.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 animate-slideInUp" style={{animationDelay: '0.4s'}}>
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Objectifs et Horizon Clairs</h3>
                    <p className="text-gray-600 text-sm">
                      Alignez vos investissements sur vos ambitions à court, moyen et long terme.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 animate-slideInUp" style={{animationDelay: '0.6s'}}>
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Gestion du Risque Maîtrisée</h3>
                    <p className="text-gray-600 text-sm">
                      Des recommandations adaptées à votre tolérance pour investir sereinement.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bouton d'action */}
              <div className="pt-4 animate-fadeInUp" style={{animationDelay: '0.8s'}}>
                <button 
                  onClick={onStartProfilRisque}
                  className="group bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <span>Démarrer mon analyse</span>
                  <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center group-hover:bg-opacity-30 transition-all duration-300">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>
            </div>

            {/* Image principale */}
            <div className="order-1 lg:order-2 relative hidden lg:block">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl animate-fadeInRight">
                <img 
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80"
                  alt="Consultation financière professionnelle entre conseillers et clients"
                  className="w-full h-[500px] object-cover transform hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                
                {/* Éléments décoratifs flottants */}
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg animate-floatUp">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-800">Investissement sécurisé</span>
                  </div>
                </div>
                
                <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg animate-floatUp" style={{animationDelay: '0.5s'}}>
                  <div className="flex items-center space-x-2">
                    <PieChart className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-800">Portfolio diversifié</span>
                  </div>
                </div>
              </div>
              
              {/* Cercle décoratif en arrière-plan */}
              <div className="absolute -top-12 -right-12 w-32 h-32 bg-emerald-200 rounded-full opacity-20 animate-pulse"></div>
              <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-blue-200 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>

          </div>
        </div>
      </section>




      {/* Strategic Financial Network Section */}
      <section className="py-12 md:py-20 relative overflow-hidden" style={{backgroundColor: '#082F49'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-16">
            <div className="inline-flex items-center bg-green-500/10 text-green-400 px-6 py-3 rounded-full text-sm font-medium mb-6 border border-green-500/20">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Plus qu'une plateforme d'investissement</span>
            </div>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              Une stratégie financière{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
                complète
              </span>
            </h2>
            <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              SOeasi ne se contente pas de vous proposer des produits d'investissement. 
              Nous orchestrons une approche globale qui intègre fiscalité, protection du risque, 
              planification de retraite et transmission patrimoniale pour une stratégie sur mesure.
            </p>
          </div>

          {/* Network Visualization */}
          <div className="relative flex items-center justify-center min-h-[400px] sm:min-h-[500px] md:min-h-[700px] lg:min-h-[800px] max-w-6xl mx-auto mt-6 md:mt-12">
            
            {/* Central Hub - Analyse Personnalisée */}
            <div className="relative z-20 animate-node-center">
              <div className="w-32 h-32 md:w-40 lg:w-48 h-32 md:h-40 lg:h-48 bg-slate-700 rounded-full border-2 md:border-4 border-green-400 flex items-center justify-center shadow-2xl relative">
                {/* Cercles concentriques décoratifs */}
                <div className="absolute inset-0 rounded-full border-2 border-green-400/20 animate-pulse"></div>
                <div className="absolute inset-2 rounded-full border border-green-400/10"></div>
                
                <div className="text-center relative z-10">
                  <div className="w-10 h-10 md:w-12 lg:w-16 h-10 md:h-12 lg:h-16 mx-auto mb-2 md:mb-3 bg-green-400 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 md:w-6 lg:w-8 h-4 md:h-6 lg:h-8 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-sm md:text-base lg:text-lg">Analyse</h3>
                  <h3 className="text-green-400 font-bold text-xs md:text-sm">Personnalisée</h3>
                </div>
              </div>
            </div>

            {/* Orbit Container avec rotation */}
            <div className="absolute inset-0 orbit-container">
              
              {/* Investissements - Position 0° (haut) */}
              <div className="node-position-1 animate-node-1">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Investissements</h4>
                    <p className="text-gray-300 text-xs mt-1">SCPI, GFI, Sukuk adaptés à votre profil</p>
                  </div>
                </div>
              </div>

              {/* Optimisation Fiscale - Position 60° (haut droite) */}
              <div className="node-position-2 animate-node-2">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Optimisation</h4>
                    <h4 className="text-white font-bold text-sm">Fiscale</h4>
                    <p className="text-gray-300 text-xs mt-1">Réduction d'impôts et optimisation</p>
                  </div>
                </div>
              </div>

              {/* Protection du Risque - Position 120° (bas droite) */}
              <div className="node-position-3 animate-node-3">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.414-4.414a2 2 0 00-2.828 0L4 16.172V20h3.828l10.586-10.586a2 2 0 000-2.828z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Protection</h4>
                    <h4 className="text-white font-bold text-sm">du Risque</h4>
                    <p className="text-gray-300 text-xs mt-1">Couverture et sécurisation du capital</p>
                  </div>
                </div>
              </div>

              {/* Gestion de Portefeuille - Position 180° (bas) */}
              <div className="node-position-4 animate-node-4">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Gestion de</h4>
                    <h4 className="text-white font-bold text-sm">Portefeuille</h4>
                    <p className="text-gray-300 text-xs mt-1">Suivi et ajustement continu</p>
                  </div>
                </div>
              </div>

              {/* Stratégie Patrimoniale - Position 240° (bas gauche) */}
              <div className="node-position-5 animate-node-5">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Stratégie</h4>
                    <h4 className="text-white font-bold text-sm">Patrimoniale</h4>
                    <p className="text-gray-300 text-xs mt-1">Transmission et valorisation</p>
                  </div>
                </div>
              </div>

              {/* Planification Retraite - Position 300° (haut gauche) */}
              <div className="node-position-6 animate-node-6">
                <div className="orbit-node">
                  <div className="w-24 h-24 bg-slate-600 rounded-full border-2 border-slate-500 flex items-center justify-center shadow-lg hover:border-green-400 transition-all duration-300 group">
                    <div className="w-12 h-12 bg-green-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <h4 className="text-white font-bold text-sm">Planification</h4>
                    <h4 className="text-white font-bold text-sm">Retraite</h4>
                    <p className="text-gray-300 text-xs mt-1">Préparation de l'avenir serein</p>
                  </div>
                </div>
              </div>

            </div>

            {/* Lignes de connexion animées */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
              <defs>
                <radialGradient id="connectionGradient" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.1"/>
                </radialGradient>
              </defs>
              
              {/* Lignes pointillées du centre vers chaque nœud */}
              <g stroke="url(#connectionGradient)" strokeWidth="2" strokeDasharray="4,4" fill="none">
                <line x1="50%" y1="50%" x2="50%" y2="15%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="0.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="85%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="50%" y2="85%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="1.5s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="75%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2s" repeatCount="indefinite"/>
                </line>
                <line x1="50%" y1="50%" x2="15%" y2="25%" opacity="0.6">
                  <animate attributeName="stroke-dasharray" values="4,4;8,2;4,4" dur="3s" begin="2.5s" repeatCount="indefinite"/>
                </line>
              </g>
            </svg>
          </div>

          {/* CTA Section */}
          <div className="text-center mt-8 md:mt-16">
            <button
              onClick={onStartSimulator}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 mx-auto"
            >
              <span>Démarrer mon analyse</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
      {/* Tutos Section */}
      <section id="tutos" className="section-background-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez comment utiliser SOeasi en 3 étapes simples pour commencer à investir et épargner efficacement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Étape 1 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Créez votre profil</h3>
              <p className="text-gray-600">
                Répondez à quelques questions sur vos objectifs financiers et votre profil de risque. 
                Notre IA analyse vos réponses en temps réel.
              </p>
            </div>

            {/* Étape 2 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <Calculator className="w-3 h-3 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Simulez vos investissements</h3>
              <p className="text-gray-600">
                Utilisez notre simulateur avancé pour voir les projections de vos investissements 
                et choisir la stratégie qui vous convient le mieux.
              </p>
            </div>

            {/* Étape 3 */}
            <div className="text-center">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-3 h-3 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Investissez et suivez</h3>
              <p className="text-gray-600">
                Commencez à investir dès 50€ et suivez vos performances en temps réel 
                avec notre tableau de bord intuitif.
              </p>
            </div>
          </div>

          {/* CTA pour commencer */}
          <div className="text-center mt-12">
            <button
              onClick={onStartSimulator}
              className="btn-primary"
            >
              <Calculator className="w-5 h-5" />
              <span>Commencer maintenant</span>
            </button>
          </div>
        </div>
      </section>


            {/* Why SOeasi Section */}
            <section className="section-background-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir SOeasi ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre plateforme vous offre tous les outils nécessaires pour investir intelligemment 
              et faire fructifier votre patrimoine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="why-icon-teal">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">100% Digital</h3>
              <p className="text-gray-600">
                Plateforme entièrement en ligne, accessible 24h/24 depuis votre smartphone, tablette ou ordinateur.
              </p>
            </div>

            <div className="text-center">
              <div className="why-icon-green">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rentable</h3>
              <p className="text-gray-600">
                Accédez à des opportunités d'investissement sélectionnées pour leur potentiel de rendement.
              </p>
            </div>

            <div className="text-center">
              <div className="why-icon-blue">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Accessible</h3>
              <p className="text-gray-600">
                Investissez dès 50€ et rejoignez une communauté de plus de 2500 investisseurs actifs.
              </p>
            </div>

            <div className="text-center">
              <div className="why-icon-purple">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automatisé</h3>
              <p className="text-gray-600">
                Investissements et épargne gérés automatiquement grâce à nos algorithmes intelligents en ligne.
              </p>
            </div>
          </div>
        </div>
      </section>




      {/* CTA Section */}
      <section className="cta-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Prêt à investir et épargner en ligne ?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Rejoignez SOeasi, la plateforme 100% digitale qui révolutionne l'investissement et l'épargne. 
            Commencez dès maintenant depuis chez vous !
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onStartSimulator}
              className="cta-btn-white"
            >
              <Calculator className="w-5 h-5" />
              <span>Faire ma simulation</span>
            </button>
            <button
              onClick={onStartChat}
              className="cta-btn-outline"
            >
              <div className="relative">
                <Bot className="w-5 h-5" />
                <Sparkles className="w-2 h-2 absolute -top-1 -right-1 text-yellow-300" />
              </div>
              <span>Parler au conseiller</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">SOeasi</h3>
              </div>
              <p className="text-gray-400">
                La plateforme digitale d'investissement et d'épargne 100% en ligne pour faire fructifier votre patrimoine.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Produits</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Simulateur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Conseiller IA</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Projets immobiliers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">SCPI</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Entreprise</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">À propos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Équipe</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Carrières</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Presse</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Centre d'aide</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Mentions légales</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Politique de confidentialité</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SOeasi. Tous droits réservés.</p>
          </div>
        </div>
      </footer>

      {/* Bouton Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 left-8 w-12 h-12 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 z-50 flex items-center justify-center group"
          aria-label="Remonter en haut de la page"
        >
          <ArrowUp className="w-6 h-6 group-hover:-translate-y-0.5 transition-transform duration-200" />
        </button>
      )}
    </div>
  );
};

export default HomePage;